/**
 * Utilitário para gerenciar URLs de imagens dos selos com fallback por ano
 */

class SeloImageManager {
  constructor() {
    this.currentYear = new Date().getFullYear();
    this.baseUrl =
      "https://slide1-images.s3.us-east-1.amazonaws.com/public/selo-medalha";
    this.imageCache = new Map();
  }

  /**
   * Gera a URL inicial da imagem (com ano atual)
   * @param {string} imageName - Nome da imagem (ex: 'seloMedalhaBronze')
   * @returns {string} URL da imagem
   */
  getImageUrl(imageName) {
    if (this.imageCache.has(imageName)) {
      return this.imageCache.get(imageName);
    }

    // Tenta primeiro com o ano atual
    const urlWithCurrentYear = `${this.baseUrl}/${imageName}${this.currentYear}.png`;
    this.imageCache.set(imageName, urlWithCurrentYear);

    return urlWithCurrentYear;
  }

  /**
   * Manipula erros de carregamento de imagem e tenta fallbacks
   * @param {Event} event - Evento de erro da imagem
   * @param {string} imageName - Nome da imagem
   * @returns {boolean} true se conseguiu fallback, false se esgotaram as opções
   */
  handleImageError(event, imageName) {
    const currentSrc = event.target.src;

    // Se falhou com o ano atual, tenta com o ano anterior
    if (currentSrc.includes(this.currentYear.toString())) {
      const urlWithPreviousYear = `${this.baseUrl}/${imageName}${
        this.currentYear - 1
      }.png`;
      event.target.src = urlWithPreviousYear;
      this.imageCache.set(imageName, urlWithPreviousYear);
      return true;
    }
    // Se falhou com o ano anterior, usa a versão padrão sem ano
    else if (currentSrc.includes((this.currentYear - 1).toString())) {
      const defaultUrl = `${this.baseUrl}/${imageName}.png`;
      event.target.src = defaultUrl;
      this.imageCache.set(imageName, defaultUrl);
      return true;
    }

    return false;
  }

  /**
   * Limpa o cache de uma imagem específica
   * @param {string} imageName - Nome da imagem
   */
  clearImageCache(imageName) {
    this.imageCache.delete(imageName);
  }

  /**
   * Limpa todo o cache de imagens
   */
  clearAllCache() {
    this.imageCache.clear();
  }

  /**
   * Pré-carrega uma imagem testando todas as variações até encontrar uma válida
   * @param {string} imageName - Nome da imagem
   * @returns {Promise<string>} URL da imagem que funciona
   */
  async preloadImage(imageName) {
    const urls = [
      `${this.baseUrl}/${imageName}${this.currentYear}.png`,
      `${this.baseUrl}/${imageName}${this.currentYear - 1}.png`,
      `${this.baseUrl}/${imageName}.png`,
    ];

    for (const url of urls) {
      try {
        await this.testImageUrl(url);
        this.imageCache.set(imageName, url);
        return url;
      } catch (error) {
        continue;
      }
    }

    // Se nenhuma URL funcionou, retorna a padrão
    const defaultUrl = `${this.baseUrl}/${imageName}.png`;
    this.imageCache.set(imageName, defaultUrl);
    return defaultUrl;
  }

  /**
   * Testa se uma URL de imagem é válida
   * @param {string} url - URL para testar
   * @returns {Promise} Promise que resolve se a imagem carrega
   */
  testImageUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Lista de nomes de medalhas disponíveis
   */
  static get MEDAL_NAMES() {
    return {
      BRONZE: "seloMedalhaBronze",
      PRATA: "seloMedalhaPrata",
      OURO: "seloMedalhaOuro",
      DIAMANTE: "seloMedalhaDiamante",
    };
  }
}

// Instância singleton para uso global
const seloImageManager = new SeloImageManager();

export default seloImageManager;
export { SeloImageManager };
